/**
 * COMMAND-mode pathfinder. Wraps EasyStar.js (^0.4.4) to plan tile-by-
 * tile paths from the player's current position to a tapped tile, then
 * feeds the next tile's direction into intentRouter under the
 * 'tap-step' source. Step 7 of the touch-controls plan.
 *
 * State machine (PathState) per eng review 1C — a discriminated union
 * on the manager so the compiler enforces exhaustive handling:
 *
 *   idle         → no path. tap a tile to enter planning.
 *   planning     → easystar is computing. resolves to walking or
 *                  unreachable (after callback) / unreachable
 *                  (watchdog after WATCHDOG_MS).
 *   walking      → executing the path. Each completed step pops the
 *                  head of `remaining`; when empty, transitions to
 *                  idle (and fires `onArrival` if the path had a
 *                  post-arrival hook, e.g. interact-after-walk).
 *   cancelled    → second-tap-on-destination. Visual feedback state
 *                  (FEEDBACK_DURATION_MS) before decaying to idle.
 *   unreachable  → either easystar returned null or planning timed
 *                  out. Same decay timing.
 *
 * Movement timing: pushNextIntent fires on the chain-press boundary
 * (WorldScene's advanceStep tail calls onStepComplete) so COMMAND
 * walks at the same 140 ms / tile cadence as keyboard / joystick.
 * Writing intent ONLY from update() would give a 1-frame gap per
 * tile — eng review's "critical" call-out at design doc line 197.
 *
 * No moving-NPC re-plan in v0.1. The grid excludes NPCs per design
 * doc (NPCs may move; baking them in produces stale plans). NPC
 * occupancy is instead applied per findPath request via EasyStar
 * avoid points (`getAvoidTiles` callback) — fresh at plan time, so
 * plans route around NPCs without the grid ever caching their
 * positions (Bug 5's "weird denial" stays fixed). If an NPC moves
 * mid-walk (none do in v0.1), the player halts on the prior tile;
 * tap again to redirect. Acceptable trade for v0.1's sparse NPC
 * traffic.
 */
import EasyStar from 'easystarjs';
import { intentRouter, type Dir } from './intentRouter';

export interface TileXY {
  x: number;
  y: number;
}

/** Optional hook fired when a successful walk completes (the
 *  pathfinder transitions walking → idle on arrival at target). Used
 *  for "tap an NPC → walk over and talk" — the WorldScene-supplied
 *  callback faces the NPC and calls tryInteract. Also used for
 *  "tap off-map across a connection edge" — the callback pushes one
 *  cardinal step in `crossEdge`, which trips the existing edge-cross
 *  flow in tryProcessMovement. */
export interface PathArrivalIntent {
  /** Tile to face on arrival (the NPC / sign foot tile). */
  faceTile?: TileXY;
  /** Cardinal direction to take one step in after arriving — used for
   *  off-map taps that resolve to an edge connection. The arrival tile
   *  itself is the in-bounds edge cell; pushing one more step in this
   *  direction lands off-map and triggers attemptConnectionTransition. */
  crossEdge?: Dir;
}

export type PathState =
  | { kind: 'idle' }
  | { kind: 'planning'; target: TileXY; sinceMs: number; intent?: PathArrivalIntent }
  | { kind: 'walking'; remaining: TileXY[]; target: TileXY; intent?: PathArrivalIntent }
  | { kind: 'cancelled'; flashTile: TileXY; sinceMs: number }
  | { kind: 'unreachable'; shakeTile: TileXY; sinceMs: number };

/** Planning watchdog — if easystar hasn't resolved within this window,
 *  we treat the target as unreachable. Catches the lib hang case
 *  (design doc 2B(b)) and any pathological grid that overruns the
 *  iteration budget. */
const WATCHDOG_MS = 1000;

/** How long a cancelled / unreachable state lingers as visual feedback
 *  before decaying to idle. Mirrors the design doc's motion-short
 *  bucket. The feedback render itself (flash / shake) is a Step 6+
 *  follow-up; v0.1 keeps the state for symmetry but renders nothing
 *  to avoid scope creep. */
const FEEDBACK_DURATION_MS = 600;

/** EasyStar's per-frame budget. 500 iterations comfortably covers a
 *  60×60 grid in one tick — the largest v0.1 map (Viridian Forest) is
 *  smaller than that. */
const ITERATIONS_PER_CALCULATION = 500;

/** Grid tile types. 0 = normal walkable; 1 = blocked; 2 = walkable but
 *  high-cost (currently: tall grass — see PATHFIND_GRASS_COST). */
const TILE_WALKABLE = 0;
const TILE_BLOCKED = 1;
const TILE_GRASS = 2;

/** Cost multiplier for stepping through tall grass. Higher = stronger
 *  avoidance. With cost 3, a 3-tile detour around grass equals walking
 *  through one grass tile — short detours preferred, long detours skipped.
 *  Tunable; bump to 5+ if friends report "too many encounters mid-path." */
const PATHFIND_GRASS_COST = 3;

export interface PathfinderCallbacks {
  /** Fired when state transitions walking → idle because the last
   *  step landed on the target. WorldScene uses this to run the
   *  interact-after-walk hook for tap-on-NPC / tap-on-sign in
   *  COMMAND mode. */
  onArrival?: (intent: PathArrivalIntent | undefined) => void;
  /** Tiles to route around, re-read at every findPath call — NPC
   *  occupancy lives here, NOT in the grid, so positions can never go
   *  stale between grid rebuilds (locked decision: don't bake NPCs
   *  into the pathfinder grid). A request targeting an avoided tile
   *  resolves unreachable, same as the old baked behavior. */
  getAvoidTiles?: () => Iterable<TileXY>;
}

export class PathfinderManager {
  private easystar: EasyStar.js;
  private state: PathState = { kind: 'idle' };
  /** Monotonic time counter driven by tick(deltaMs). Used by the
   *  watchdog + feedback-decay clocks. Independent of wall clock so
   *  the pathfinder still ticks deterministically under Phaser's
   *  fixed delta. */
  private elapsedMs = 0;
  private callbacks: PathfinderCallbacks;
  /** Cancellation handle from easystar.findPath — passed to
   *  cancelPath when we replan, so a stale callback doesn't fire
   *  against a new target. */
  private inFlightId: number | null = null;

  constructor(grid: number[][], callbacks: PathfinderCallbacks = {}) {
    this.easystar = new EasyStar.js();
    this.easystar.setGrid(grid);
    // 0 = normal walkable, 2 = grass walkable (high-cost). 1 = blocked
    // is excluded from acceptable tiles so paths route around walls.
    // NPCs are avoided per-request (getAvoidTiles), not via the grid.
    this.easystar.setAcceptableTiles([TILE_WALKABLE, TILE_GRASS]);
    this.easystar.setTileCost(TILE_GRASS, PATHFIND_GRASS_COST);
    this.easystar.setIterationsPerCalculation(ITERATIONS_PER_CALCULATION);
    // Sync mode: the path callback fires inside calculate() instead
    // of being deferred via setTimeout(0). Two reasons:
    //   1. The chain-press hook needs the new path's first intent
    //      set BEFORE tryProcessMovement reads it on the same tick.
    //      Async would add a one-frame gap.
    //   2. Tests are deterministic — pf.tick(16) resolves the path
    //      callback synchronously rather than depending on the
    //      event-loop turn.
    // V0.1 grids cap at 60×60 and easystar's iteration budget (500)
    // covers that comfortably in one frame, so sync doesn't risk a
    // frame stall.
    this.easystar.enableSync();
    // Movement is strictly orthogonal (4-way) per the rest of the
    // engine — keyboard cursors, classic D-pad, joystick all snap to
    // cardinals. EasyStar's diagonals are off by default; explicit
    // here for documentation.
    this.easystar.disableDiagonals();
    this.callbacks = callbacks;
  }

  getState(): Readonly<PathState> {
    return this.state;
  }

  /**
   * Begin a path from (fromX, fromY) to `target`. Same-target second-
   * tap while walking → cancel (per design doc 7B). Otherwise replan
   * — any in-flight planning is dropped via easystar.cancelPath.
   *
   * `intent` carries an optional post-arrival hook for tap-on-NPC /
   * tap-on-sign: the WorldScene callback faces + interacts once the
   * walk completes.
   */
  findPath(
    fromX: number,
    fromY: number,
    target: TileXY,
    intent?: PathArrivalIntent,
  ): void {
    if (
      this.state.kind === 'walking' &&
      this.state.target.x === target.x &&
      this.state.target.y === target.y
    ) {
      this.cancel();
      return;
    }
    if (
      this.state.kind === 'planning' &&
      this.state.target.x === target.x &&
      this.state.target.y === target.y
    ) {
      // Duplicate tap on the same target while still planning — no-op.
      return;
    }

    // Drop any prior path (in-flight calc + held intent).
    this.discardInFlight();
    intentRouter.clearSource('tap-step');

    // Refresh NPC avoidance for THIS plan — positions are read at
    // request time so they're never stale (see PathfinderCallbacks).
    this.easystar.stopAvoidingAllAdditionalPoints();
    if (this.callbacks.getAvoidTiles) {
      for (const t of this.callbacks.getAvoidTiles()) {
        this.easystar.avoidAdditionalPoint(t.x, t.y);
      }
    }

    this.state = {
      kind: 'planning',
      target,
      sinceMs: this.elapsedMs,
      intent,
    };
    this.inFlightId = this.easystar.findPath(
      fromX,
      fromY,
      target.x,
      target.y,
      (path) => {
        this.inFlightId = null;
        // Guard against a stale callback firing after we replanned /
        // cancelled — state changed in the meantime.
        if (
          this.state.kind !== 'planning' ||
          this.state.target.x !== target.x ||
          this.state.target.y !== target.y
        ) {
          return;
        }
        // Per easystarjs: null = unreachable; empty = already there.
        if (!path || path.length === 0) {
          this.state = {
            kind: 'unreachable',
            shakeTile: target,
            sinceMs: this.elapsedMs,
          };
          return;
        }
        if (path.length === 1) {
          // Start tile only — already at target. Fire arrival hook if
          // any (tap-on-adjacent-NPC routed through findPath would
          // hit this branch).
          const arriveIntent = this.state.intent;
          this.state = { kind: 'idle' };
          this.callbacks.onArrival?.(arriveIntent);
          return;
        }
        // Strip the start tile — `remaining` is the future steps.
        const remaining = path.slice(1);
        this.state = {
          kind: 'walking',
          remaining,
          target,
          intent: this.state.intent,
        };
        // Push intent for the first step immediately so the next
        // frame's tryProcessMovement reads it.
        this.pushNextIntent(fromX, fromY);
      },
    );
  }

  /** Discard whatever path is in flight (cancelled by user OR
   *  pre-empted by replan). Visual feedback state if there was
   *  something live; otherwise idle. */
  cancel(): void {
    if (this.state.kind === 'walking' || this.state.kind === 'planning') {
      const flashTile = this.state.target;
      this.discardInFlight();
      intentRouter.clearSource('tap-step');
      this.state = {
        kind: 'cancelled',
        flashTile,
        sinceMs: this.elapsedMs,
      };
    }
  }

  /** Hard reset — used on map transitions + on Phaser SLEEP. Clears
   *  in-flight calcs, intent, and state without leaving a feedback
   *  flash behind. */
  reset(): void {
    this.discardInFlight();
    intentRouter.clearSource('tap-step');
    this.state = { kind: 'idle' };
  }

  /**
   * Called from WorldScene.advanceStep right after a step completes.
   * Pops the head of `remaining`; if more steps left, sets intent for
   * the next; if the path's done, transitions to idle + fires
   * onArrival.
   *
   * (playerX, playerY) is the *current* player tile — what's been
   * stamped by stepTo at step start. The next step's direction is
   * remaining[0] relative to (playerX, playerY).
   */
  onStepComplete(playerX: number, playerY: number): void {
    if (this.state.kind !== 'walking') return;
    this.state.remaining.shift();
    if (this.state.remaining.length === 0) {
      const arriveIntent = this.state.intent;
      intentRouter.clearSource('tap-step');
      this.state = { kind: 'idle' };
      this.callbacks.onArrival?.(arriveIntent);
      return;
    }
    this.pushNextIntent(playerX, playerY);
  }

  /**
   * Per-frame tick. Drives easystar.calculate(), runs the planning
   * watchdog, and decays cancelled/unreachable feedback states.
   * WorldScene.update() calls this once per frame before reading
   * intent.
   */
  tick(deltaMs: number): void {
    this.elapsedMs += deltaMs;
    this.easystar.calculate();

    if (
      this.state.kind === 'planning' &&
      this.elapsedMs - this.state.sinceMs > WATCHDOG_MS
    ) {
      const target = this.state.target;
      this.discardInFlight();
      this.state = {
        kind: 'unreachable',
        shakeTile: target,
        sinceMs: this.elapsedMs,
      };
    }

    if (
      (this.state.kind === 'cancelled' || this.state.kind === 'unreachable') &&
      this.elapsedMs - this.state.sinceMs > FEEDBACK_DURATION_MS
    ) {
      this.state = { kind: 'idle' };
    }
  }

  /** Public helper for handleTap to convert an absolute tile target
   *  into the right pathfinder action. Pure data shape, no side
   *  effects. */
  static directionTo(fromX: number, fromY: number, to: TileXY): Dir {
    const dx = to.x - fromX;
    const dy = to.y - fromY;
    if (dy < 0) return 'up';
    if (dy > 0) return 'down';
    if (dx < 0) return 'left';
    return 'right';
  }

  private pushNextIntent(currentX: number, currentY: number): void {
    if (this.state.kind !== 'walking') return;
    const next = this.state.remaining[0];
    if (!next) return;
    const dir = PathfinderManager.directionTo(currentX, currentY, next);
    // Clear the source first so a 90° turn doesn't leave the prior
    // direction set alongside the new one (would confuse the OR view
    // in intentRouter for one tick).
    intentRouter.clearSource('tap-step');
    intentRouter.setIntent(dir, true, 'tap-step');
  }

  private discardInFlight(): void {
    if (this.inFlightId !== null) {
      this.easystar.cancelPath(this.inFlightId);
      this.inFlightId = null;
    }
  }
}

/** Build the EasyStar grid from a runtime map. Mirrors WorldScene.canStep's
 *  walkability rule. Warp tiles override their collision=1 default so
 *  doors / staircases are valid pathfind targets.
 *
 *  Grid values:
 *    0 = normal walkable
 *    1 = blocked (wall, water)
 *    2 = walkable but high-cost (tall grass — encounters)
 *
 *  Both 0 and 2 are passed to setAcceptableTiles; 2 carries an extra
 *  per-step cost via setTileCost so paths route around grass when a
 *  reasonable detour exists, but still walk through grass when it's
 *  the only route.
 *
 *  The grid is PURE TERRAIN — NPC occupancy is deliberately NOT baked
 *  in (locked decision: NPCs may move; a baked grid caches stale
 *  positions between rebuilds). Bug 5's "weird denial" (easystar plans
 *  through an NPC, canStep blocks the first step) is instead prevented
 *  by PathfinderManager's request-time avoid points — see
 *  PathfinderCallbacks.getAvoidTiles.
 */
export function buildPathfinderGrid(
  mapData: {
    width: number;
    height: number;
    collision: number[];
    behaviors: number[];
    warps: ReadonlyArray<{ x: number; y: number }>;
  },
  impassableBehaviors: ReadonlySet<number>,
  options?: {
    /** Behavior ids that are walkable but high-cost (typically grass).
     *  Defaults to none. */
    highCostBehaviors?: ReadonlySet<number>;
  },
): number[][] {
  const warpKeys = new Set(mapData.warps.map((w) => `${w.x},${w.y}`));
  const highCostBehaviors = options?.highCostBehaviors;
  const grid: number[][] = [];
  for (let y = 0; y < mapData.height; y++) {
    const row: number[] = [];
    for (let x = 0; x < mapData.width; x++) {
      const idx = y * mapData.width + x;
      if (warpKeys.has(`${x},${y}`)) {
        row.push(TILE_WALKABLE); // warp tile — walkable as pathfind target
        continue;
      }
      const c = mapData.collision[idx] ?? 1;
      if (c !== 0) {
        row.push(TILE_BLOCKED);
        continue;
      }
      const b = mapData.behaviors[idx] ?? 0;
      if (impassableBehaviors.has(b)) {
        row.push(TILE_BLOCKED);
        continue;
      }
      if (highCostBehaviors?.has(b)) {
        row.push(TILE_GRASS);
        continue;
      }
      row.push(TILE_WALKABLE);
    }
    grid.push(row);
  }
  return grid;
}
